import { useState } from 'react';

import { useFieldWiring } from '@nesso/ui/hooks/use-field-wiring';
import { cn } from '@nesso/ui/lib/utils';
import { Calendar } from '@nesso/ui/primitives/calendar';
import { Field, FieldSlot } from '@nesso/ui/primitives/field';
import { inputInvalidClassName } from '@nesso/ui/primitives/input';
import { Popover, PopoverContent, PopoverTrigger } from '@nesso/ui/primitives/popover';
import { selectTriggerClassName } from '@nesso/ui/primitives/select';

import { formatLocalDate, parseLocalDate } from '@/shared/lib/time-utils';

import { formatScheduleSessionDateTrigger } from '../../../lib/format-schedule-session-date-trigger';

type Props = {
    value: string;
    error?: boolean | string;
    onChange: (value: string) => void;
};

// Popover + Calendar, as `NessoPlanDatePicker` does, but past dates stay selectable (no `minDate`).
// The trigger draws no icon — it lives in the row's icon column instead — so padding goes to 16px.
export function ScheduleSessionDatePicker({ value, error, onChange }: Props) {
    const [open, setOpen] = useState(false);
    const wiring = useFieldWiring({ error });

    const selectedDate = value ? parseLocalDate(value) : undefined;

    return (
        <Field data-invalid={wiring.dataInvalid}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger
                    {...wiring.controlProps}
                    type="button"
                    className={cn(
                        selectTriggerClassName,
                        inputInvalidClassName,
                        'bg-input-background justify-start px-4',
                    )}
                >
                    <span>
                        {selectedDate
                            ? formatScheduleSessionDateTrigger(selectedDate)
                            : 'Select a date'}
                    </span>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                        mode="single"
                        selected={selectedDate}
                        defaultMonth={selectedDate}
                        onSelect={(date) => {
                            if (!date) return;
                            onChange(formatLocalDate(date));
                            setOpen(false);
                        }}
                    />
                </PopoverContent>
            </Popover>
            <FieldSlot errorMessage={wiring.errorMessage} errorId={wiring.errorId} />
        </Field>
    );
}
